/**
 * Integrations Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Integrations, DreameIntegration } from '../models/Integrations.model';

export class IntegrationsService {
  private firestore: FirestoreService;
  private readonly COLLECTION = 'integrations';
  private readonly DREAME_DOC_ID = 'dreameCloud';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  /**
   * Get Dreame integration configuration
   */
  async getDreameIntegration(): Promise<Integrations> {
    const doc = await this.firestore.getDocument(this.COLLECTION, this.DREAME_DOC_ID);
    return doc ? Integrations.fromFirestore(doc) : new Integrations();
  }

  /**
   * Update Dreame integration configuration
   */
  async updateDreameIntegration(updates: Partial<DreameIntegration>): Promise<void> {
    const integrations = await this.getDreameIntegration();
    integrations.updateDreameIntegration(updates);
    await this.firestore.saveDocument(
      this.COLLECTION,
      this.DREAME_DOC_ID,
      integrations.toFirestore()
    );
  }

  /**
   * Save complete Dreame integration configuration
   */
  async saveDreameIntegration(config: Partial<DreameIntegration>): Promise<Integrations> {
    const integrations = await this.getDreameIntegration();

    // Update configuration
    integrations.updateDreameIntegration({
      ...config,
      updatedAt: new Date(),
    });

    await this.firestore.saveDocument(
      this.COLLECTION,
      this.DREAME_DOC_ID,
      integrations.toFirestore()
    );

    return integrations;
  }

  /**
   * Subscribe to Dreame integration changes
   */
  subscribeToDreameIntegration(callback: (integrations: Integrations) => void): () => void {
    return this.firestore.subscribeToDocument(
      this.COLLECTION,
      this.DREAME_DOC_ID,
      (doc) => {
        const integrations = doc ? Integrations.fromFirestore(doc) : new Integrations();
        callback(integrations);
      }
    );
  }

  /**
   * Update Dreame connection test results
   */
  async updateDreameTestResults(
    status: 'success' | 'error',
    message: string
  ): Promise<void> {
    const integrations = await this.getDreameIntegration();
    integrations.updateDreameIntegration({
      lastTestedAt: new Date(),
      lastTestStatus: status,
      lastTestMessage: message,
    });

    await this.firestore.saveDocument(
      this.COLLECTION,
      this.DREAME_DOC_ID,
      integrations.toFirestore()
    );
  }
}
